import {
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import ChecklistCard from '../components/check-list/ChecklistCard';
import { Button, ButtonGroup, Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import { checkListConfig } from '../config/checkListConfig';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { useCountCrawlChecklistQuery } from '@/app/Crawl/api/checklistsApi';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import CheckListGrid from '../components/check-list/CheckListGrid';
import NoCheckListSelected from '../components/check-list/ChecklistEmpty';
import CheckListCardSkeleton from '../components/check-list/CheckListCardSkeleton';
import PageDrawer from '../components/pages/PageDrawer';
import DataLoadError from '@/components/errors/DataLoadError';
import { shouldShowDataLoadError, isNetworkApiError } from '@/app/common/utils/apiErrorUtils';
import NoCrawlData from '../components/common/NoCrawlData';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import CheckListTable from '../components/check-list/CheckListTable';
import useDomain from '@/hooks/useDomain';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import type {
  ChecklistConfigWithCount,
  ChecklistItemData
} from '../components/check-list/checklistTypes';

type BreadcrumbItem = {
  text: string;
  href: string;
};

type CrawlOutletContext = [
  BreadcrumbItem[],
  Dispatch<SetStateAction<BreadcrumbItem[]>>
];

type ChecklistCount = {
  category: string;
  count: number;
};

const CheckList = () => {
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const access = useToolAccess('crawl');
  const [, setBreadcrumbData] = useOutletContext<CrawlOutletContext>();
  useEffect(() => {
    setBreadcrumbData([{ text: 'فهرست بازبینی', href: '#' }]);
  }, []);
  const [searchText, setSearchText] = useState('');
  const { domain_slug } = useDomain();
  const { slug } = useParams();
  useGetUserInfoQuery();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string | null>('card');
  const job_page = slug === 'last' ? domain_slug : slug;

  // اسکرول به بالا حین تغییر پارامتر issue
  useEffect(() => {
    if (searchParams.get('issues')) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [searchParams.get('issues')]);

  const {
    data: rawData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useCountCrawlChecklistQuery({ job_page, domain_slug }, {
    skip: access.isLoading || !access.haveAccess
  });
  const data = rawData as {
    data?: {
      checklist_categories?: ChecklistCount[];
      craw_status?: number | null;
    };
  } | undefined;

  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return <ToolAccessDenied access={access} tool_label="ابزار خزش" />;
  }

  if (shouldShowDataLoadError(isError, error)) {
    return (
      <DataLoadError
        onRetry={() => refetch()}
        isRetrying={isFetching}
        isNetworkError={isNetworkApiError(error)}
      />
    );
  }
  if (!isLoading && !data?.data) return <NoCrawlData />;

  const countData = (data?.data?.checklist_categories ??
    []) as ChecklistCount[];
  const crawl_status = data?.data?.craw_status;
  // قرار دادن عدد برای کارت های فهرست بازبینی
  const checkListConfigWithCount = (
    status: number | null | undefined
  ): ChecklistConfigWithCount => {
    const config = checkListConfig(status) as unknown as ChecklistConfigWithCount;
    return {
    errors: [
      ...config.errors.map((item: ChecklistItemData) => {
        const count = countData?.find(
          (countItem: ChecklistCount) => countItem.category === item.title
        )?.count;
        return { ...item, count: count ? count : 0 };
      })
    ],
    warning: [
      ...config.warning.map((item: ChecklistItemData) => {
        const count = countData?.find(
          (countItem: ChecklistCount) => countItem.category === item.title
        )?.count;
        return { ...item, count: count ? count : 0 };
      })
    ],
    info: [
      ...config.info.map((item: ChecklistItemData) => {
        const count = countData?.find(
          (countItem: ChecklistCount) => countItem.category === item.title
        )?.count;
        return { ...item, count: count ? count : 0 };
      })
    ]
    };
  };

  return (
    <Tab.Container
      // مشخص کردن تب فعال در صورت وجود پیش‌فرض در پارامتر
      activeKey={activeTab}
      // مشخص کردن تب فعال در صورت وجود پیش‌فرض در پارامتر
      onSelect={e => {
        setActiveTab(e);
      }}
    >
      <Row className="mt-4 g-0 px-0 mx-0 mb-3">
        <Col lg={4} md={0}>
          <Form.Control
            size="sm"
            style={{
              // width: 250,
              visibility: 'hidden'
            }}
          />
        </Col>
        <Col lg={4} md={12}>
          <Nav
            variant="tabs"
            style={{
              borderLeft: '0px solid var(--falcon-border-color)'
            }}
            className="d-flex position-sticky top-0 border-bottom-0 flex-column"
          >
            <ButtonGroup>
              <Nav.Item
                as={Button}
                onClick={() => setActiveTab('card')}
                active={activeTab === 'card'}
                variant="outline-primary"
              >
                نمایش کارتی
              </Nav.Item>
              <Nav.Item
                as={Button}
                onClick={() => setActiveTab('table')}
                active={activeTab === 'table'}
                variant="outline-primary"
              >
                نمایش جدولی
              </Nav.Item>
            </ButtonGroup>
          </Nav>
        </Col>
        <Col lg={4} md={12}>
          <div className="d-flex justify-content-end">
            <Form.Control
              className="my-3 my-md-0"
              style={{
                zIndex: 1019,
                // width: 250
                maxWidth: isMdUp ? 200 : '100%'
              }}
              placeholder="جستجو بر اساس نام"
              onChange={e => setSearchText(e.target.value)}
              value={searchText}
            />
          </div>
        </Col>
      </Row>
      {isLoading ? (
        <Row className="g-3 mb-3">
          {/* کارت های لودینگ فهرست بازبینی */}
          <Col md={12} xl={4}>
            <CheckListCardSkeleton
              title="خطا"
              color="danger"
              tooltip={crawlTooltip.crawlerCheckList.errors}
              items={[15, 15, 20, 22, 30, 32, 35, 50, 33, 38, 27, 20, 22, 35]}
            />
          </Col>
          <Col md={6} xl={4}>
            <CheckListCardSkeleton
              title="هشدار"
              color="warning"
              tooltip={crawlTooltip.crawlerCheckList.warning}
              items={[
                20, 22, 21, 24, 30, 23, 31, 33, 25, 24, 29, 28, 22, 22, 22, 26,
                20
              ]}
            />
          </Col>
          <Col md={6} xl={4}>
            <CheckListCardSkeleton
              title="اطلاعیه"
              color="info"
              tooltip={crawlTooltip.crawlerCheckList.info}
              items={[
                22, 20, 21, 20, 18, 19, 20, 26, 27, 23, 26, 18, 18, 14, 20, 18
              ]}
            />
          </Col>
        </Row>
      ) : (
        <Tab.Content>
          <Tab.Pane eventKey="card">
            <Row className="g-3 mb-3">
              <Col md={12} xl={4}>
                {/* کارت خطاهای فهرست بازبینی */}
                <ChecklistCard
                  searchText={searchText}
                  icon="times-circle"
                  color="danger"
                  title="خطا"
                  count={
                    countData?.find(
                      (item: ChecklistCount) => item.category === 'error'
                    )?.count
                  }
                  data={checkListConfigWithCount(crawl_status).errors}
                  tooltip={crawlTooltip.crawlerCheckList.errors}
                />
              </Col>
              <Col md={6} xl={4}>
                {/* کارت هشدار های فهرست بازبینی */}
                <ChecklistCard
                  searchText={searchText}
                  icon="exclamation-circle"
                  color="warning"
                  title="هشدار"
                  count={
                    countData?.find(
                      (item: ChecklistCount) => item.category === 'warning'
                    )?.count
                  }
                  data={checkListConfigWithCount(crawl_status).warning}
                  tooltip={crawlTooltip.crawlerCheckList.warning}
                />
              </Col>
              {/* کارت اطلاعیه های فهرست بازبینی */}
              <Col md={6} xl={4}>
                <ChecklistCard
                  searchText={searchText}
                  icon="info-circle"
                  color="info"
                  title="اطلاعیه"
                  count={
                    countData?.find(
                      (item: ChecklistCount) => item.category === 'info'
                    )?.count
                  }
                  data={checkListConfigWithCount(crawl_status).info}
                  tooltip={crawlTooltip.crawlerCheckList.info}
                />
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane eventKey="table">
            <CheckListTable
              searchText={searchText}
              checkListConfigWithCount={checkListConfigWithCount(crawl_status)}
            />
          </Tab.Pane>
        </Tab.Content>
      )}

      {!isLoading && (
        <Row>
          <Col>
            {searchParams.get('issues') || searchParams.get('activeTab') ? (
              crawl_status && <CheckListGrid crawl_status={crawl_status} />
            ) : (
              <NoCheckListSelected />
            )}
          </Col>
        </Row>
      )}
      <PageDrawer />
    </Tab.Container>
  );
};

export default CheckList;
